import { useCallback, useContext, useLayoutEffect, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { MainAreaRightShiftContext } from '../components/Chat.SidePanel/context';
import { useOnResize } from './useOnResize';

// Anchors the transcript comment sidebar to the bottom of whatever element is given
// as the ref (typically the transcript's tab strip), tracking both its own resizes and
// shifts caused by the chat side panel opening/closing.
export const useCommentsBounds = () => {
  const [element, ref] = useState<HTMLDivElement>();
  const [bounds, setBounds] = useState<RefPoint>({
    top: 0,
    left: 0,
    viewportLeft: 0,
    width: 0,
  });

  // The sidebar column has a fixed width (see Tab.Transcript.module.css), so opening or
  // closing the chat side panel shifts its position without changing its own size —
  // ResizeObserver only fires on size changes, so it wouldn't otherwise notice the shift.
  // Re-observing whenever this changes forces a fresh read of the sidebar's position.
  const chatPanelShift = useContext(MainAreaRightShiftContext);

  const updateBounds = useCallback((el: HTMLDivElement) => {
    if (el) {
      const rect = el.getBoundingClientRect();

      setBounds({
        top: el.offsetTop + rect.height + 20,
        left: el.offsetLeft,
        viewportLeft: rect.left,
        width: rect.width,
      });
    }
  }, []);

  const [observer] = useState(
    () =>
      new ResizeObserver(([entry]: ResizeObserverEntry[]) => {
        if (entry) {
          updateBounds(entry.target as HTMLDivElement);
        }
      })
  );

  useLayoutEffect(() => {
    if (!element) return;
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [element, observer, chatPanelShift]);

  const handleResize = useCallback(() => {
    updateBounds(element);
  }, [element, updateBounds]);

  useOnResize(handleResize);

  return [ref, bounds] as const;
};

type RefPoint = {
  top: number;
  left: number;
  /** `left` is offset-parent relative, for the body-portaled sidebar items; this one is not. */
  viewportLeft: number;
  width: number;
};
